import React, { useEffect, useState } from "react";
import { Box, Button, Paper, Typography, Chip } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";

// Formate un écart en millisecondes en secondes lisibles, ex: "0.85 s"
function formatGap(ms) {
    if (ms == null) return null;
    return `${(ms / 1000).toFixed(2)} s`;
}

export default function DuelCard({
                                     duel,
                                     onWinnerSelected,
                                     onPhotoFinishToggle,
                                     onJuryDecisionToggle,
                                     onSetActive,
                                     onClearActive,
                                     isActive,
                                 }) {
    const { id, athleteA, athleteB, winner, colorA, colorB, photoFinish, juryDecision, arrivalGapMs } = duel;

    // La card se verrouille automatiquement dès qu'un gagnant existe — il
    // faut un clic explicite sur "Débloquer" pour pouvoir la modifier à
    // nouveau. Se reverrouille automatiquement dès qu'un nouveau gagnant
    // arrive (clic manuel ou détection auto), pour éviter les doubles clics
    // accidentels une fois le duel tranché.
    const [unlocked, setUnlocked] = useState(false);
    useEffect(() => {
        setUnlocked(false);
    }, [winner?.fiscode]);

    const isLocked = !!winner && !unlocked;

    const renderAthlete = (ath) => {
        if (!ath) return "—";
        if (ath.bye) return "BYE";
        const bib = ath.bib ? `[${ath.bib}]` : "";
        const rank = ath.qualifRank ? ` (${ath.qualifRank})` : "";
        return `${bib} ${ath.name}${rank}`;
    };

    const colorChip = (color) => {
        if (!color) return null;
        return (
            <Chip
                label={color.toUpperCase()}
                color={color === "blue" ? "primary" : "error"}
                size="small"
                sx={{ ml: 1 }}
            />
        );
    };

    const gapLabel = formatGap(arrivalGapMs);
    const isWinnerA = !!winner && !!athleteA && winner.fiscode === athleteA.fiscode;
    const isWinnerB = !!winner && !!athleteB && winner.fiscode === athleteB.fiscode;

    return (
        <Paper
            sx={{
                p: 2,
                width: 260,
                borderLeft: isActive
                    ? "6px solid #2e7d32"
                    : colorA || colorB
                        ? "6px solid #1976d2"
                        : "6px solid transparent"
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2">
                    {id}
                </Typography>
                {isActive && <Chip label="🏁 LIVE" color="success" size="small" />}
                {isLocked && <Chip label="🔒 Locked" size="small" variant="outlined" />}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography>{renderAthlete(athleteA)}</Typography>
                {colorChip(colorA)}
            </Box>

            {!isLocked ? (
                <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => onWinnerSelected(id, athleteA)}
                    disabled={!athleteA || athleteA.bye}
                >
                    Winner A
                </Button>
            ) : (
                !isWinnerA &&
                gapLabel && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Gap: {gapLabel}
                    </Typography>
                )
            )}

            <Box sx={{ my: 1, textAlign: "center" }}>vs</Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography>{renderAthlete(athleteB)}</Typography>
                {colorChip(colorB)}
            </Box>

            {!isLocked ? (
                <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => onWinnerSelected(id, athleteB)}
                    disabled={!athleteB || athleteB.bye}
                >
                    Winner B
                </Button>
            ) : (
                !isWinnerB &&
                gapLabel && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Gap: {gapLabel}
                    </Typography>
                )
            )}

            {!isLocked && (
                <Button
                    variant={photoFinish ? "contained" : "outlined"}
                    color="warning"
                    size="small"
                    sx={{ mt: 2, width: "100%" }}
                    onClick={() => onPhotoFinishToggle(id, !photoFinish)}
                >
                    {photoFinish ? "Photo finish enabled" : "Enable photo finish"}
                </Button>
            )}

            {!isLocked && onJuryDecisionToggle && (
                <Button
                    variant={juryDecision ? "contained" : "outlined"}
                    color="info"
                    size="small"
                    sx={{ mt: 1, width: "100%" }}
                    onClick={() => onJuryDecisionToggle(id, !juryDecision)}
                >
                    {juryDecision ? "Jury decision enabled" : "Enable jury decision"}
                </Button>
            )}

            {!isLocked &&
                onSetActive &&
                !winner &&
                athleteA &&
                athleteB &&
                !athleteA.bye &&
                !athleteB.bye && (
                    <Button
                        variant={isActive ? "contained" : "outlined"}
                        color="success"
                        size="small"
                        sx={{ mt: 1, width: "100%" }}
                        onClick={() => (isActive ? onClearActive() : onSetActive(id))}
                    >
                        {isActive
                            ? "Cancel (duel in progress)"
                            : "🏁 Mark as duel in progress (auto-detect)"}
                    </Button>
                )}

            <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
                Winner: {winner ? renderAthlete(winner) : "—"}
            </Typography>

            {winner && (photoFinish || juryDecision) && (
                <Chip
                    label={photoFinish ? "🏁 Photo finish" : "⚖️ Jury decision"}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                />
            )}

            {isLocked && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LockOpenIcon fontSize="small" />}
                    sx={{ mt: 1, width: "100%" }}
                    onClick={() => setUnlocked(true)}
                >
                    Unlock to edit
                </Button>
            )}
        </Paper>
    );
}